import { Badge, Box, Button, Group, Table, Text, Tooltip } from "@mantine/core";
import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";

import type { KpiOutliersResponse } from "@msc/shared";

import { hasRole, useCurrentUser } from "../../hooks/useAuth.js";
import { useAddTeamMembers } from "../../hooks/useKpi.js";

/**
 * Heatmap-Tabelle der Ausreißer-Analyse: eine Zeile pro Kunde/Berater,
 * eine Spalte pro Monat. Zell-Färbung relativ zum Zeilen-Maximum (blau),
 * geflaggte Zellen (Abweichung > Schwelle vom rollierenden 6-Monats-Median)
 * mit rotem Rahmen. Erste Spalte sticky, Rest scrollt horizontal.
 */

const MONTH_SHORT = [
  "Jan", "Feb", "Mär", "Apr", "Mai", "Jun",
  "Jul", "Aug", "Sep", "Okt", "Nov", "Dez",
];

function monthLabel(ym: string): string {
  const [y, m] = ym.split("-");
  return `${MONTH_SHORT[Number(m) - 1]} ${y!.slice(2)}`;
}

function fmtCompact(v: number): string {
  if (v === 0) return "–";
  if (Math.abs(v) >= 1000) return `${Math.round(v / 1000)}k`;
  return String(Math.round(v));
}

const fmtEur = (v: number) => `${Math.round(v).toLocaleString("de-DE")} €`;

const STICKY: CSSProperties = {
  position: "sticky",
  left: 0,
  background: "var(--mantine-color-body)",
  zIndex: 1,
};

export function OutlierMatrix({ data }: { data: KpiOutliersResponse }) {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const addTeamMembers = useAddTeamMembers();
  // Team-Zuordnung (Home-Bereich) ist Führungsaufgabe — wie im Team-Tab.
  const canAssignTeam = hasRole(user, [
    "admin",
    "geschaeftsleitung",
    "bereichsleiter",
    "stellvertreter",
  ]);

  if (data.rows.length === 0) {
    return (
      <Text c="dimmed" ta="center" py="md">
        {t("pages.kpi.outliers.empty")}
      </Text>
    );
  }

  return (
    <Box style={{ overflowX: "auto" }}>
      <Table withTableBorder withColumnBorders striped={false}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={{ ...STICKY, minWidth: 220 }}>
              {data.mode === "customer"
                ? t("pages.kpi.outliers.colCustomer")
                : t("pages.kpi.outliers.colEmployee")}
            </Table.Th>
            <Table.Th style={{ minWidth: 90 }}>
              {t("pages.kpi.outliers.colTotal")}
            </Table.Th>
            {data.months.map((ym) => (
              <Table.Th key={ym} style={{ minWidth: 64, textAlign: "right" }}>
                {monthLabel(ym)}
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {data.rows.map((row) => {
            const rowMax = Math.max(...row.cells.map((c) => c.value), 1);
            return (
              <Table.Tr key={row.key}>
                <Table.Td style={STICKY}>
                  <Group gap={6} wrap="nowrap">
                    <Text size="sm" fw={500} lineClamp={1}>
                      {row.label}
                    </Text>
                    {row.careerLevel && (
                      <Badge size="xs" variant="light" color="gray">
                        {row.careerLevel}
                      </Badge>
                    )}
                    {row.external && (
                      <Tooltip
                        label={t("pages.kpi.outliers.noEmployeeHint")}
                        openDelay={200}
                      >
                        <Badge size="xs" variant="light" color="orange">
                          {t("pages.kpi.outliers.externalBadge")}
                        </Badge>
                      </Tooltip>
                    )}
                    {/* „+ Team": SF-bekannten Mitarbeiter per Klick dem
                        Bereich zuordnen (Home-Bereich, wie im Team-Tab). */}
                    {data.mode === "employee" &&
                      canAssignTeam &&
                      row.employeeId &&
                      (row.homeSegmentId === data.segmentId ? (
                        <Tooltip
                          label={t("pages.kpi.outliers.inTeamTooltip")}
                          openDelay={200}
                        >
                          <Badge size="xs" variant="light" color="green">
                            ✓ {t("pages.kpi.outliers.inTeamBadge")}
                          </Badge>
                        </Tooltip>
                      ) : (
                        <Tooltip
                          label={t("pages.kpi.outliers.addToTeamTooltip")}
                          openDelay={200}
                        >
                          <Button
                            size="compact-xs"
                            variant="light"
                            loading={
                              addTeamMembers.isPending &&
                              addTeamMembers.variables?.employeeIds[0] ===
                                row.employeeId
                            }
                            onClick={() =>
                              addTeamMembers.mutate({
                                segmentId: data.segmentId,
                                employeeIds: [row.employeeId!],
                              })
                            }
                          >
                            {t("pages.kpi.outliers.addToTeamButton")}
                          </Button>
                        </Tooltip>
                      ))}
                  </Group>
                </Table.Td>
                <Table.Td style={{ textAlign: "right" }}>
                  <Text size="xs" fw={600}>
                    {fmtCompact(row.total)}
                  </Text>
                </Table.Td>
                {row.cells.map((cell, i) => {
                  const intensity = cell.value > 0 ? cell.value / rowMax : 0;
                  const details =
                    cell.median != null
                      ? t("pages.kpi.outliers.cellTooltip", {
                          value: fmtEur(cell.value),
                          median: fmtEur(cell.median),
                          window: data.medianWindow,
                          delta:
                            cell.deltaPct == null
                              ? "–"
                              : `${cell.deltaPct >= 0 ? "+" : ""}${cell.deltaPct.toFixed(0)} %`,
                        })
                      : fmtEur(cell.value);
                  return (
                    <Tooltip
                      key={data.months[i]}
                      label={
                        cell.flag
                          ? t("pages.kpi.outliers.flagTooltip", { details })
                          : details
                      }
                      openDelay={300}
                    >
                      <Table.Td
                        style={{
                          textAlign: "right",
                          background:
                            intensity > 0
                              ? `rgba(234, 91, 11, ${0.06 + intensity * 0.38})`
                              : undefined,
                          boxShadow: cell.flag
                            ? "inset 0 0 0 2px var(--mantine-color-red-6)"
                            : undefined,
                        }}
                      >
                        <Text
                          size="xs"
                          fw={cell.flag ? 700 : undefined}
                          c={cell.flag ? "red.8" : undefined}
                        >
                          {fmtCompact(cell.value)}
                        </Text>
                      </Table.Td>
                    </Tooltip>
                  );
                })}
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Box>
  );
}
